import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

import {
  FeatureFlag,
  useNoUserFeatureFlagWithStatus,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvestorInvestorInvitationCheck } from "@vega-api/generated/auth-invitation-investor-investor-v1/auth-invitation-investor-investor-v1";
import { useInvitationCheck } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { LPAuthPaths } from "@vega-modules/paths";

import { InvalidCodeMessage } from "./components/InvalidCodeMessage";
import { RegistrationLayout } from "./components/RegistrationLayout";
import { LPRegistrationForm } from "./LPRegistrationForm";

export const LPRegistrationLegacy = () => {
  // Pre-auth read: this page is unauthenticated, so the post-auth
  // useFeatureFlag never resolves here. While it loads, both check queries stay
  // disabled so we never fire a request for the wrong flow.
  const { isEnabled: isInvitationV2, isLoading: isFlagLoading } =
    useNoUserFeatureFlagWithStatus(FeatureFlag.GP_AUTH_INVITATION_V2);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationCode = searchParams.get("invitationCode");

  // Both hooks are called unconditionally (stable hook order); each query is
  // enabled only for its flag state so just one request fires. Selecting the
  // hook function by flag instead would swap the query when the flag settles
  // and tear down the in-flight request.
  const v2Check = useInvestorInvestorInvitationCheck(
    { invitationCode: invitationCode! },
    {
      query: {
        retry: 2,
        enabled: !isFlagLoading && isInvitationV2 && isTruthy(invitationCode),
        refetchOnWindowFocus: false,
      },
    },
  );

  // --- Legacy (auth-invitation-v1). Delete with the v2 selection below once
  // gp_auth_invitation_v2 is fully rolled out. ---
  const legacyCheck = useInvitationCheck(
    { invitationCode: invitationCode! },
    {
      query: {
        retry: 2,
        enabled: !isFlagLoading && !isInvitationV2 && isTruthy(invitationCode),
        refetchOnWindowFocus: false,
      },
    },
  );

  const { data, isError, isLoading, isFetched } = isInvitationV2
    ? v2Check
    : legacyCheck;

  const isRegistrationComplete =
    isError && Boolean(sessionStorage.getItem("userRegistrationComplete"));

  useEffect(() => {
    if (isRegistrationComplete) {
      sessionStorage.removeItem("userRegistrationComplete");
      navigate(LPAuthPaths.Login, { replace: true });
    }
  }, [isRegistrationComplete, navigate]);

  if (isFalsy(invitationCode)) {
    return (
      <RegistrationLayout>
        <InvalidCodeMessage />
      </RegistrationLayout>
    );
  }

  if (isLoading || !isFetched) {
    return <LoadingSpinner />;
  }

  return (
    <RegistrationLayout>
      {isError ? (
        <InvalidCodeMessage />
      ) : (
        <LPRegistrationForm
          invitationCheckData={data}
          invitationCode={invitationCode}
        />
      )}
    </RegistrationLayout>
  );
};
